import {
  COOKIE_CONSENT_API_PATH,
  COOKIE_CONSENT_STORAGE_KEY,
  type CookieConsentRequestBody,
  type CookieConsentResponse,
  type CookieConsentSource,
} from '~/types/cookieConsent';
import { shouldShowCookieConsentBanner } from '~/utils/cookieConsentRoutes';

const MOBILE_AUTO_CLOSE_MS = 5000;

function hasStoredConsent(): boolean {
  if (!import.meta.client) {
    return false;
  }

  try {
    return Boolean(localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY));
  } catch {
    return false;
  }
}

function persistConsent(): void {
  if (!import.meta.client) {
    return;
  }

  try {
    localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, new Date().toISOString());
  } catch (error) {
    console.error('Failed to persist cookie consent:', error);
  }
}

export function useCookieConsent() {
  const route = useRoute();
  const { apiQuery } = useApi();
  const authStore = useAuthStore();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const isVisible = ref(false);
  const isHydrated = ref(false);
  let autoCloseTimer: ReturnType<typeof setTimeout> | null = null;
  let isDismissed = false;
  let scrollListenerAttached = false;

  const clearAutoCloseTimer = () => {
    if (autoCloseTimer !== null) {
      clearTimeout(autoCloseTimer);
      autoCloseTimer = null;
    }
  };

  const isRouteAllowed = computed(() =>
    shouldShowCookieConsentBanner(route.path, route.meta?.layout as string | undefined),
  );

  const evaluateVisibility = () => {
    if (!import.meta.client) {
      isVisible.value = false;
      return;
    }

    if (isDismissed || hasStoredConsent() || !isRouteAllowed.value) {
      isVisible.value = false;
      clearAutoCloseTimer();
      return;
    }

    isVisible.value = true;
  };

  const recordConsentOnServer = async (source: CookieConsentSource) => {
    if (!authStore.isAuth) {
      authStore.getFingerprint();
    }

    const body: CookieConsentRequestBody = {
      source,
      acceptedAt: new Date().toISOString(),
      pageUrl: window.location.href,
      ...(!authStore.isAuth && authStore.fingerprint ? { guestId: authStore.fingerprint } : {}),
    };

    try {
      await apiQuery<CookieConsentResponse>(COOKIE_CONSENT_API_PATH, 'POST', { body });
    } catch (error) {
      console.error('Failed to record cookie consent:', error);
    }
  };

  const dismiss = (source: CookieConsentSource) => {
    if (isDismissed) {
      return;
    }

    isDismissed = true;
    clearAutoCloseTimer();
    detachScrollListener();
    persistConsent();
    isVisible.value = false;
    recordConsentOnServer(source);
  };

  const scheduleAutoClose = () => {
    clearAutoCloseTimer();

    if (!isVisible.value || !isMobile.value) {
      return;
    }

    autoCloseTimer = setTimeout(() => {
      dismiss('auto_close');
    }, MOBILE_AUTO_CLOSE_MS);
  };

  const onScrollDismiss = () => {
    if (!isVisible.value || !isMobile.value || isDismissed) {
      return;
    }

    dismiss('scroll');
  };

  const attachScrollListener = () => {
    if (!import.meta.client || scrollListenerAttached) {
      return;
    }

    window.addEventListener('scroll', onScrollDismiss, { passive: true });
    scrollListenerAttached = true;
  };

  const detachScrollListener = () => {
    if (!import.meta.client || !scrollListenerAttached) {
      return;
    }

    window.removeEventListener('scroll', onScrollDismiss);
    scrollListenerAttached = false;
  };

  watch(
    [isRouteAllowed, isMobile],
    () => {
      if (!isHydrated.value) {
        return;
      }

      evaluateVisibility();

      if (isVisible.value && isMobile.value) {
        scheduleAutoClose();
        attachScrollListener();
      } else {
        detachScrollListener();
      }
    },
  );

  onMounted(() => {
    isHydrated.value = true;
    evaluateVisibility();

    if (isVisible.value && isMobile.value) {
      scheduleAutoClose();
      attachScrollListener();
    }
  });

  onBeforeUnmount(() => {
    clearAutoCloseTimer();
    detachScrollListener();
  });

  return {
    isVisible: readonly(isVisible),
    isMobile,
    dismiss,
  };
}
